import { useEffect, useState } from "react";
import { Navigate, Route, Routes, useNavigate } from "react-router-dom"; // <-- No BrowserRouter here!

import { useAuth, useAuthUpdate, User } from "contexts/AuthContext";
import { jwtDecode } from "jwt-decode";
import CondactLegalResearch from "pages/conductLegalResearch/condactLegalResearch";
import CoordinatorCreateRequest from "pages/conductLegalResearch/coordinatorCreateRequest";
import CreateInternalNewRequest from "pages/conductLegalResearch/createInternalNewRequest";
import CreateNewCommitteeRequest from "pages/conductLegalResearch/createNewCommitteeRequest";
import CreateNewRequest from "pages/conductLegalResearch/createNewRequest";
import InternalConductLegalResearch from "pages/conductLegalResearch/fragments/internalLegalResearch";
import SingleCommitteeConductLegalResearch from "pages/conductLegalResearch/singleCommitteeLegalResearch";
import SingleConductLegalResearch from "pages/conductLegalResearch/singleConductLegalResearch";
import LegalResearchCoordinatorDashboard from "pages/conductLegalResearch/LegalResearchCoordinatorDashboard";
import LegalResearchProsecutorDashboard from "pages/conductLegalResearch/LegalResearchProsecutorDashboard";
import CreateRequest from "pages/lawImplementation/createNewRequest";
import LawImplementation from "pages/lawImplementation/lawImplementation";
import SingleAuditLawImplementation from "pages/lawImplementation/singleAuditLawImplementation";
import SingleLawImplementation from "pages/lawImplementation/singleLawImplementation";
import LegalAdvisory from "pages/LegalAdvisory/legalAdvisory";
import CreateLegalAdvisoryRequest from "pages/LegalAdvisory/request";
import SingleLegalAdvisory from "pages/LegalAdvisory/singlelegalAdvisory";
import LegalAwarenessCreationRequest from "pages/legalAwarenessCreation/createNewRequest";
import LegalAwarenessCreation from "pages/legalAwarenessCreation/legalAwarenessCreation";
import LegalAwarenessCreationZone from "pages/legalAwarenessCreation/legalAwarenessCreationZone";
import SingleLegalAwarenessCreation from "pages/legalAwarenessCreation/singleLegalAwarenessCreation";
import SingleLegalAwarenessCreationZone from "pages/legalAwarenessCreation/singleLegalAwarenessCreationZone";
import SingleWoredaAwareness from "pages/legalAwarenessCreation/singleWoredaAwareness";
import CreateLegalTraingProvisionInternalRequest from "pages/legalTraningProvision/internal_request";
import LegalTraningProvision from "pages/legalTraningProvision/legalTraningProvision";
import LegalTraningProvisionZone from "pages/legalTraningProvision/LegalTraningProvisionZone";
import CreateLegalTraingProvisionRequest from "pages/legalTraningProvision/request";
import SingleLegalTraningProvisionZone from "pages/legalTraningProvision/singleLegalTrainingProvisionZone";
import SingleLegalTraningProvision from "pages/legalTraningProvision/singleLegalTraningProvision";
import LegalTranslation from "pages/legalTranslation/legalTranslation";
import CreateLegalTranslation from "pages/legalTranslation/request";
import SingleLegalTranslation from "pages/legalTranslation/singleLegalTranslation";
import LegalTranslationProsecutorDashboard from "pages/legalTranslation/legalTranslationProsecutorDashboard";
import LegalTranslationCordinatorDashboard from "pages/legalTranslation/legalTranslationCordinatorDashboard";
import ImpAwarenessCordinatorDashboard from "pages/legalTranslation/impAwarenessCordinatorDashboard";
import ImpAwarenessProsecutorDashboard from "pages/legalTranslation/impAwarenessProsecutorDashboard";
import DirectorLegislativeDraftRequest from "pages/legislativeDrafting/directorLegislativeDraftRequest";
import LegislativeDrafting from "pages/legislativeDrafting/legislativeDrafting";
import CreateLegislativeDraftingRequest from "pages/legislativeDrafting/request";
import SingleLegislativeDraft from "pages/legislativeDrafting/singlelegislativeDraft";
import RegisterDirectives from "pages/registerDirectives/registerDirectives";
import RegisterDirectiveRequest from "pages/registerDirectives/request";
import SingleRegisterDirectives from "pages/registerDirectives/singleRegisterDirectives";
import LegalDraftingPapers from "pages/Researches/LegalDrafting/legalDraftingPapers";
import LegalTranslationPapers from "pages/Researches/LegalTranslation/legalTranslationPapers";
import Researches from "pages/Researches/researches";
import PublicLawImplementation from "pages/lawImplementation/publicLawImplementation";
import PublicRegisterDirectives from "pages/registerDirectives/publicRegisterDirectives";
import CreateSectorDirectiveRequest from "pages/sectorDirectives/request";
import SectorDirectives from "pages/sectorDirectives/sectorDirectives";
import SingleSectorDirectives from "pages/sectorDirectives/singlesectorDirectives";
import { useLocation } from "react-router-dom";
import { shouldShowSidebar } from "utils/constantValues";
import Header from "./components/Header";
import Sidebar from "./components/Sidebar";
import IncidentList from "./pages/IncidentList";
import IncidentRegister from "./pages/IncidentRegister/IncidentRegister";
import IncidentRequest from "./pages/IncidentRequest/IncidentRequest";
import Dashboard from "./pages/dashboard/Dashboard";
import RoleBasedDashboard from "./components/RoleBasedDashboard";
import useLanguageListener from "./hooks/useLanguageListener";
import SessionTimeout from "./components/SessionTimeout";
export interface JwtPayload {
  exp?: number; // Expiration time in seconds
  iat?: number; // Issued at
  sub?: string; // Subject (user id, etc.)
  [key: string]: any; // Any additional claims
}

export function isValidJwtFormat(token: string): boolean {
  if (!token || typeof token !== "string") return false;

  const parts = token.split(".");
  if (parts.length !== 3) return false;

  // Check if all parts are non-empty
  return parts.every((part) => part.trim().length > 0);
}

export function getTokenPayloadSafely(
  token: string | null | undefined,
): JwtPayload | null {
  if (!token || !isValidJwtFormat(token)) {
    console.warn("Invalid or missing token format");
    return null;
  }

  try {
    return jwtDecode<JwtPayload>(token);
  } catch (error) {
    console.error("Error decoding token:", error);
    return null;
  }
}

export function decodeAccessToken(
  token: string | null | undefined,
): JwtPayload | null {
  if (!token) return null;

  try {
    return getTokenPayloadSafely(token);
  } catch (err) {
    console.error("Failed to decode access token:", err);

    // Fallback to manual decoding
    try {
      const tokenParts = token.split(".");
      if (tokenParts.length !== 3) return null;

      const base64Url = tokenParts[1];
      if (!base64Url) return null;

      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => "%" + c.charCodeAt(0).toString(16).padStart(2, "0"))
          .join(""),
      );

      return JSON.parse(jsonPayload) as JwtPayload;
    } catch (fallbackErr) {
      console.error(
        "Failed to decode access token with fallback method:",
        fallbackErr,
      );
      return null;
    }
  }
}
export default function App() {
  useLanguageListener();

  const navigate = useNavigate();
  const contextData = useAuth();
  const updateAuthContext = useAuthUpdate();
  const [internalUserType, setInternalUserType] = useState<boolean>(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [tokenLoaded, setTokenLoaded] = useState(false);
  const location = useLocation();
  const showSidebar = shouldShowSidebar(location.pathname);

  useEffect(() => {
    // console.log("handle message effect");
    function handleMessage(event: MessageEvent) {
      // console.log(
      //   "token:",
      //   event.data?.token || event?.data?.data?.access_token
      // );
      // console.log("data", event.data);
      // console.log("event", event);
      if (event.data?.type === "SET_TOKEN") {
        if (event.data.token && isValidJwtFormat(event.data.token)) {
          sessionStorage.setItem("authToken", event.data.token);
          // console.log("Token received and stored");
          const payload = getTokenPayloadSafely(event.data.token);
          const userData = {
            id: payload?.user_id?.toString() ?? "",
            user_id: payload?.user_id?.toString() ?? "",
            employee_id: payload?.employee_id?.toString() ?? "",
            admin_unit: payload?.admin_unit?.toString() ?? "",
            org_unit: payload?.org_unit?.toString() ?? "",
            user_type: payload?.user_type?.toString() ?? "external",
            access: event.data.token,
            access_token: event.data.token,
            roles: payload?.roles || payload?.roles,
            full_name: payload?.full_name,
            username: payload?.username,
            permissions: payload?.permissions,
            org_unit_id: payload?.organization?.uu_id,
            org_phone_number: payload?.organization?.org_phone_number,
            organization: payload?.organization,
            //refresh: response.refresh_token,
          };
          updateAuthContext({
            ...contextData,
            token: event.data.token,
            user: userData,
          });
          sessionStorage.setItem("authUser", JSON.stringify(userData));
          // console.log("Token payload:", payload);
          if (payload?.user_type === "internal") {
            setInternalUserType(true);
          }
          setTokenLoaded(true);
        } else {
          console.error("Invalid token received in SET_TOKEN message");
          setTokenLoaded(true);
        }
      }
      if (event.data?.type === "SET_SESSION_DATA_FOR_STAKEHOLDERS") {
        console.log(
          "Debug: Received SET_SESSION_DATA_FOR_STAKEHOLDERS message:",
          event.data,
        );
        if (event.data.data.access_token) {
          sessionStorage.setItem("authToken", event.data.data.access_token);
          // Use the detail from the message for user data. The client portal
          // sends only the token, so fall back to the token's own claims
          // (without them the stakeholder had no id or organization, and its
          // lists and dashboard could not be scoped to it).
          const detail = event.data.data.detail;
          const claims: any = getTokenPayloadSafely(event.data.data.access_token) || {};
          const organization = detail?.organization ?? claims?.organization;
          const userId = (detail?.uu_id ?? claims?.user_id)?.toString() ?? "";
          const firstName = detail?.first_name ?? claims?.first_name;
          const lastName = detail?.last_name ?? claims?.last_name;
          const userData = {
            id: userId,
            user_id: userId,
            employee_id: userId,
            admin_unit: (organization?.admin_unit ?? claims?.admin_unit)?.toString() ?? "",
            org_unit: (organization?.admin_unit ?? claims?.org_unit)?.toString() ?? "",
            user_type: "external", // Assume external for stakeholders
            access_token: event.data.data.access_token,
            access: event.data.data.access_token,
            roles: detail?.role ? [detail.role] : claims?.roles || [],
            full_name:
              claims?.full_name ||
              [firstName, lastName].filter(Boolean).join(" "),
            username: detail?.email ?? claims?.username,
            permissions: [],
            org_unit_id: organization?.uu_id,
            org_phone_number: organization?.org_phone_number,
            organization,
            uu_id: userId,
            first_name: firstName,
            last_name: lastName,
            email: detail?.email ?? claims?.email,
            is_police: detail?.is_police ?? claims?.is_police,
            // Add other fields as needed
          };
          console.log("Debug: Constructed userData:", userData);
          updateAuthContext({
            ...contextData,
            token: event.data.data.access_token,
            user: userData,
          });
          sessionStorage.setItem("authUser", JSON.stringify(userData));
          setTokenLoaded(true);
        } else {
          console.error(
            "Invalid token received in SET_SESSION_DATA_FOR_STAKEHOLDERS message",
          );
          setTokenLoaded(true);
        }
      }
    }

    window.addEventListener("message", handleMessage);

    return () => window.removeEventListener("message", handleMessage);
  }, []);

  // Handshake: actively ask the parent portal for the token instead of passively
  // waiting for it. This removes the race where the parent posts SET_TOKEN before
  // this iframe's listener is ready, and the reliance on sessionStorage surviving
  // (which some browsers block/partition for embedded iframes). The parent must
  // answer APP_READY by sending its usual SET_TOKEN message. If the parent ignores
  // it, behavior is unchanged.
  useEffect(() => {
    // Already authenticated — no need to ask.
    if (contextData.token || contextData.user?.access_token) return;
    // Not embedded (running standalone) — nothing to ask.
    if (typeof window === "undefined" || window.parent === window) return;

    const requestToken = () => {
      try {
        window.parent.postMessage({ type: "APP_READY" }, "*");
      } catch (e) {
        // Cross-origin restrictions — safe to ignore.
      }
    };

    requestToken(); // ask immediately on mount
    let attempts = 0;
    const interval = setInterval(() => {
      attempts += 1;
      requestToken();
      if (attempts >= 15) clearInterval(interval); // stop after ~12s
    }, 800);

    // When the token arrives, contextData.token changes, this effect re-runs and
    // the cleanup below clears the interval.
    return () => clearInterval(interval);
  }, [contextData.token, contextData.user?.access_token]);

  // Manage body class for mobile sidebar
  useEffect(() => {
    if (sidebarOpen) {
      document.body.classList.add("sidebar-open");
    } else {
      document.body.classList.remove("sidebar-open");
    }

    return () => {
      document.body.classList.remove("sidebar-open");
    };
  }, [sidebarOpen]);

  useEffect(() => {
    // Skip if we already have a token
    if (contextData.token || contextData.user.access_token) {
      setTokenLoaded(true);
      return;
    }

    // Try loading from various sessionStorage keys
    const tokenString =
      sessionStorage.getItem("authToken") ||
      sessionStorage.getItem("accessToken");

    if (tokenString && isValidJwtFormat(tokenString)) {
      try {
        const payload = getTokenPayloadSafely(tokenString);
        console.log("Debug: Decoded payload from sessionStorage:", payload);

        // Check if we have user details in sessionStorage
        const userString = sessionStorage.getItem("authUser");
        let userData;

        if (userString) {
          // Use stored user data if available
          userData = JSON.parse(userString);
        } else if (payload) {
          // Construct user data from payload
          userData = {
            id: payload?.user_id?.toString() ?? "",
            user_id: payload?.user_id?.toString() ?? "",
            employee_id: payload?.user_id?.toString() ?? "",
            admin_unit: payload?.organization?.admin_unit?.toString() ?? "",
            org_unit: payload?.organization?.admin_unit?.toString() ?? "",
            user_type: payload?.user_type || "external",
            access_token: tokenString,
            access: tokenString,
            roles: payload?.roles || [],
            full_name:
              payload?.full_name ||
              `${payload?.first_name || ""} ${payload?.last_name || ""}`,
            username: payload?.username || payload?.email,
            permissions: payload?.permissions || [],
            org_unit_id: payload?.organization?.uu_id,
            org_phone_number: payload?.organization?.org_phone_number,
            organization: payload?.organization,
            uu_id: payload?.uu_id,
            first_name: payload?.first_name,
            last_name: payload?.last_name,
            email: payload?.email,
            is_police: payload?.is_police,
          };
        }

        if (userData) {
          updateAuthContext({
            ...contextData,
            token: tokenString,
            user: userData,
          });

          if (userData.user_type === "internal") {
            setInternalUserType(true);
          }
        }

        setTokenLoaded(true);
      } catch (e) {
        console.error("Failed to decode token from sessionStorage:", e);
        setTokenLoaded(true);
      }
    } else {
      setTokenLoaded(true);
    }
  }, []); // Remove contextData from dependencies to prevent infinite loops

  const handleLoginSuccess = () => {
    setIsAuthenticated(true);
    navigate("/conduct-legal-research"); // Redirect to main page after login
  };

  const handleLogout = () => {
    sessionStorage.removeItem("authToken");
    sessionStorage.removeItem("authUser");
    setIsAuthenticated(false);
    updateAuthContext({
      ...contextData,
      token: "",
      user: {} as User,
    });
  };
  const isResearchesPage =
    location.pathname === "/researchs" ||
    location.pathname === "/legalTranslationPapers" ||
    location.pathname === "/legalDraftingPapers" ||
    location.pathname === "/registerDirectivesPapers" ||
    location.pathname === "/lawImplementationPapers";

  //if (!isAuthenticated) {
  //    return <LoginPage onLoginSuccess={handleLoginSuccess} />;
  //}

  const appRoutes = (
    <>
      <SessionTimeout app="research" />
    <Routes>
      <Route path="/" element={<RoleBasedDashboard />} />
      <Route path="/incident-list" element={<IncidentList />} />
      <Route path="/incident-request" element={<IncidentRequest />} />
      <Route path="/incident-register" element={<IncidentRegister />} />
      <Route path="/researchs" element={<Researches />} />
      <Route
        path="/legalTranslationPapers"
        element={<LegalTranslationPapers />}
      />
      <Route path="/legalDraftingPapers" element={<LegalDraftingPapers />} />
      <Route path="/registerDirectivesPapers" element={<PublicRegisterDirectives />} />
      <Route
        path="/lawImplementationPapers"
        element={<PublicLawImplementation />}
      />
      <Route
        path="/stakeholders"
        element={<Navigate to="/conduct-legal-research" replace />}
      />
      <Route
        path="/conduct-legal-research/external"
        element={<CondactLegalResearch />}
      />
      <Route
        path="/conduct-legal-research/archived"
        element={<CondactLegalResearch />}
      />
      <Route
        path="/conduct-legal-research/legal-research"
        element={<CondactLegalResearch />}
      />
      <Route
        path="/conduct-legal-research/committee"
        element={<CondactLegalResearch />}
      />
      <Route
        path="/conduct-legal-research/internal"
        element={<CondactLegalResearch />}
      />
      <Route
        path="/conduct-legal-research/internal-topics"
        element={<CondactLegalResearch />}
      />
      <Route
        path="/conduct-legal-research/proposal-submission"
        element={<CondactLegalResearch />}
      />
      <Route
        path="/conduct-legal-research/scheduled"
        element={<CondactLegalResearch />}
      />
       {/* conduct legal research routes */}
       <Route path="/conduct-legal-research">
         <Route index element={<CondactLegalResearch />} />
         <Route path=":id" element={<SingleConductLegalResearch />} />
         <Route path="request" element={<CreateNewRequest />} />
         <Route
           path="committe-request"
           element={<CreateNewCommitteeRequest />}
         />
         <Route
           path="editInternalrequest/:id"
           element={<CreateInternalNewRequest />}
         />
         <Route path="internalRequest" element={<CoordinatorCreateRequest />} />
         <Route path="internal/:id" element={<InternalConductLegalResearch />} />
         <Route
           path="committee/:id"
           element={<SingleCommitteeConductLegalResearch />}
         />
<Route
            path="coordinator-dashboard"
            element={<LegalResearchCoordinatorDashboard />}
          />
          <Route
            path="prosecutor-dashboard"
            element={<LegalResearchProsecutorDashboard />}
          />
        </Route>
      <Route path="/legislative-drafting">
        <Route index element={<LegislativeDrafting />} />
        <Route
          index
          path="request"
          element={<CreateLegislativeDraftingRequest />}
        />
        <Route path=":id" element={<SingleLegislativeDraft />} />
        <Route
          path="director-request"
          element={<DirectorLegislativeDraftRequest />}
        />
      </Route>
      <Route path="/legal-advisory">
        <Route index element={<LegalAdvisory />} />
        <Route index path="request" element={<CreateLegalAdvisoryRequest />} />
        <Route path=":id" element={<SingleLegalAdvisory />} />
        <Route
          path="director-request"
          element={<DirectorLegislativeDraftRequest />}
        />
      </Route>
      <Route path="/sector-directives">
        <Route index element={<SectorDirectives />} />
        <Route
          index
          path="request"
          element={<CreateSectorDirectiveRequest />}
        />
        <Route path=":id" element={<SingleSectorDirectives />} />
        <Route
          path="director-request"
          element={<DirectorLegislativeDraftRequest />}
        />
      </Route>
      <Route path="/registering-directives">
        <Route index element={<RegisterDirectives />} />
        <Route index path="request" element={<RegisterDirectiveRequest />} />
        <Route path=":id" element={<SingleRegisterDirectives />} />
        <Route
          path="director-request"
          element={<DirectorLegislativeDraftRequest />}
        />
      </Route>
       <Route path="/legal-translation">
         <Route index element={<LegalTranslation />} />
         <Route path="external" element={<LegalTranslation />} />
         <Route path="external/:id" element={<SingleLegalTranslation />} />
         <Route path="external/request" element={<CreateLegalTranslation />} />
         <Route path="internal" element={<LegalTranslation />} />
         <Route path="internal/:id" element={<SingleLegalTranslation />} />
         <Route path="internal/request" element={<CreateLegalTranslation />} />
         <Route path="request" element={<CreateLegalTranslation />} />
         <Route path=":id" element={<SingleLegalTranslation />} />
         <Route
           path="director-request"
           element={<DirectorLegislativeDraftRequest />}
         />
         <Route
           path="prosecutor-dashboard"
           element={<LegalTranslationProsecutorDashboard />}
         />
         <Route
           path="cordinator-dashboard"
           element={<LegalTranslationCordinatorDashboard />}
         />
         <Route
           path="imp-awareness-dashboard"
           element={<ImpAwarenessCordinatorDashboard />}
         />
         <Route
           path="imp-awareness-prosecutor-dashboard"
           element={<ImpAwarenessProsecutorDashboard />}
         />
       </Route>
      <Route path="/law-implementation">
        <Route index element={<LawImplementation />} />
        <Route path="request" element={<CreateRequest />} />
        <Route path=":id" element={<SingleLawImplementation />} />
        <Route path="audit/:id" element={<SingleAuditLawImplementation />} />
        <Route
          path="director-request"
          element={<DirectorLegislativeDraftRequest />}
        />
      </Route>
      <Route path="/legal-awareness-creation">
        <Route index element={<LegalAwarenessCreation />} />
        <Route path="request" element={<LegalAwarenessCreationRequest />} />
        <Route path="woreda/:id" element={<SingleWoredaAwareness />} />
        <Route path=":id" element={<SingleLegalAwarenessCreation />} />
        <Route
          path="director-request"
          element={<DirectorLegislativeDraftRequest />}
        />
      </Route>
      <Route path="/legal-training-provision">
        <Route index element={<LegalTraningProvision />} />
        <Route
          index
          path="request"
          element={<CreateLegalTraingProvisionRequest />}
        />
        <Route
          index
          path="internal-request"
          element={<CreateLegalTraingProvisionInternalRequest />}
        />
        <Route path=":id" element={<SingleLegalTraningProvision />} />
        <Route
          path="director-request"
          element={<DirectorLegislativeDraftRequest />}
        />
      </Route>
      <Route path="/legal-training-provision-zone">
        <Route index element={<LegalTraningProvisionZone />} />
        <Route
          index
          path="request"
          element={<CreateLegalTraingProvisionRequest />}
        />
        <Route
          index
          path="internal-request"
          element={<CreateLegalTraingProvisionInternalRequest />}
        />
        <Route path=":id" element={<SingleLegalTraningProvisionZone />} />
        <Route
          path="director-request"
          element={<DirectorLegislativeDraftRequest />}
        />
      </Route>
      <Route path="/legal-awareness-creation-zone">
        <Route index element={<LegalAwarenessCreationZone />} />
        <Route
          index
          path="request"
          element={<CreateLegalTraingProvisionRequest />}
        />
        <Route
          index
          path="internal-request"
          element={<CreateLegalTraingProvisionInternalRequest />}
        />
        <Route path=":id" element={<SingleLegalAwarenessCreationZone />} />
        <Route
          path="director-request"
          element={<DirectorLegislativeDraftRequest />}
        />
      </Route>
    </Routes>
    </>
  );

  return !internalUserType ? (
    // External User Layout - Fixed Header on top, Fixed Sidebar below header
    <div className="h-screen bg-gray-100">
      {/* Fixed Header */}
      <Header isInternalUser={false} />

      {/* Fixed Sidebar */}
      {showSidebar && (
        <div className="hidden md:block">
          <Sidebar isInternalUser={false} />
        </div>
      )}
      {/* Mobile Sidebar Overlay */}
      {showSidebar && sidebarOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div
            className="fixed inset-0 bg-gray-600 bg-opacity-75"
            onClick={() => setSidebarOpen(false)}
          />
          <div className="fixed inset-y-0 left-0 z-50 w-64">
            <Sidebar isInternalUser={false} />
          </div>
        </div>
      )}

      {/* Main Content - positioned to account for fixed header and sidebar */}
      <main
        className={`relative z-10 pt-16 overflow-y-auto h-full p-4 md:p-6 ${
          isResearchesPage ? "md:pl-20" : "md:pl-72"
        }`}
      >
        {/* Mobile Menu Button */}
        {showSidebar && (
          <div className="md:hidden mb-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="mobile-menu-btn"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            </button>
          </div>
        )}
        {appRoutes}
      </main>
    </div>
  ) : (
    // Internal User Layout - Fixed Sidebar on left, Fixed Header on right
    <div className="h-screen flex bg-gray-100">
      {/* Fixed Sidebar */}
      {showSidebar && (
        <div className="hidden md:block">
          <Sidebar isInternalUser />
        </div>
      )}

      {/* Mobile Sidebar Overlay */}
      {showSidebar && sidebarOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div
            className="fixed inset-0 bg-gray-600 bg-opacity-75"
            onClick={() => setSidebarOpen(false)}
          />
          <div className="fixed inset-y-0 left-0 z-50 w-64">
            <Sidebar isInternalUser />
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <div className="flex flex-col flex-1 overflow-hidden">
        {/* Fixed Header */}
        <Header isInternalUser={true} />

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto p-4 md:p-6 md:pl-72 pt-16 md:pt-24 ">
          {/* Mobile Menu Button */}
          {showSidebar && (
            <div className="md:hidden mb-4">
              <button
                onClick={() => setSidebarOpen(true)}
                className="mobile-menu-btn"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              </button>
            </div>
          )}
          {appRoutes}
        </main>
      </div>
    </div>
  );
}
